import {identity} from '@packages/utilities/fp/utils';
import {
  Result,
  Plugins,
  TypeImplementation,
  type Type,
  EnvTypes,
} from '@packages/systems/wfdl';
import {_$, call1} from '@packages/utilities/wfdl-helpers';
import {
  functionOf,
  TGeneric,
  TTypeApplication,
  TTypeConstructor,
  TTypeReference,
} from '@packages/systems/wfdl-ast';
import {pluginName, qualify, typeName} from '@packages/systems/wfdl-Qualified';

const tyOperand = TTypeConstructor(
  qualify(pluginName('ConditionsOperand'), typeName('Operand'))
);

export const TOperand = (arg: Type) =>
  TTypeApplication({
    con: tyOperand,
    arg,
  });

export const toOperandExpr = _$('ConditionsOperand', 'ToOperand');
export const FromOperand = call1(_$('ConditionsOperand', 'FromOperand'));

/**
 * Defines a plugin value for a `T => Operand<T>` implicit function.
 *
 * Adding this implicit function in a plugin for a type makes all values of that
 * type immediately available as conditional group conditions.
 *
 * @param ty - The type that needs to be integrated with conditional conditions.
 */
export const operand = (ty: Type): EnvTypes.SafeTypedValue => ({
  envType: {
    dataType: functionOf(ty, TOperand(ty)),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  expression: toOperandExpr,
});

export const plugin = Plugins.PrivilegedPlugin({
  dataTypes: {
    /**
     * A nominally unique type that under the hood is a function used as the
     * first argument passed to `Conditions::["Apply predicate"]`.
     *
     * Using a nominally unique type for operands prevents them from popping
     * up in unexpected places. This is because the type-system doesn’t see them
     * as functions and so it does not try to show calls to these functions in
     * binding menus.
     *
     * This type is accompanied by 2 functions defined below:
     * - `ToOperand` which constructs an `Operand` value out of a value.
     * - `FromOperand` which given an `Operand` value returns back the
     *   original value.
     */
    Operand: TypeImplementation.Custom({
      params: ['a'],
      parseValue: (_) =>
        Result.Error(
          'Cannot use Literal expressions to create Operand values.'
        ),
      render: () => null,
    }),
  },

  values: {
    ToOperand: {
      envType: {
        dataType: TGeneric({
          arg: 'x',
          body: functionOf(TTypeReference('x'), TOperand(TTypeReference('x'))),
        }),
        exported: 'hidden',
      },
      value: identity,
    },

    FromOperand: {
      envType: {
        dataType: TGeneric({
          arg: 'x',
          body: functionOf(TOperand(TTypeReference('x')), TTypeReference('x')),
        }),
        exported: 'hidden',
      },
      value: identity,
    },
  },
});
